import { Field, Input, Label, RadioGroup } from "@headlessui/react";
import { useIntlT, useTranslate } from "@lobby/ocb-intl";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";

import { Payment } from "../../../../../entities/payment";
import {
  APIError,
  Alert,
  Button,
  Icon,
  Spinner,
  type TPaymentMethodsList,
  emitter,
  first,
} from "../../../../../shared";
import { NoDataPlaceholder, ProfileSection } from "../../components";

import trcErcBancImage from "./TRC_ERC_Bank.png";
import {
  CryptoCurrencyBlock,
  CryptoWalletBlock,
  PIXBillingInformation,
  PayPalBillingInformation,
  PaymentMethodCard,
} from "./components";
import { countrySelectorOptions, penniesToDollars, selectPaymentAccountComponent } from "./helpers";
import { WithdrawTabRest } from "./wallet.withdraw.rest";

import type { FormEvent } from "react";

const THEMES_WITH_REST_WITHDRAW = ["localhost", "richbet", "abet", "flexbet", "maxibet"];
const LOBBY_THEME = (import.meta as ImportMeta & { env?: { VITE_THEME?: string } }).env?.VITE_THEME;
const USE_REST_WITHDRAW = THEMES_WITH_REST_WITHDRAW.includes(LOBBY_THEME ?? "");

export function WithdrawTab() {
  // if (USE_REST_WITHDRAW) {
  //   return <WithdrawTabRest />;
  // }

  return <WithdrawTabLegacy />;
}

export function WithdrawTabLegacy() {
  const { $t } = useTranslate();

  const { data, isLoading } = Payment.useMethodList("withdraw");
  const activeWithdrawRequestResponse = Payment.useActiveWithdrawRequest();

  return (
    <ProfileSection
      isLoading={isLoading || activeWithdrawRequestResponse.isLoading}
      data={data}
      placeholder={
        <NoDataPlaceholder
          topText={$t({
            defaultMessage: "Currently, there are no available methods for withdrawing funds.",
          })}
          bottomText={$t({
            defaultMessage: "Please contact the support chat to withdraw funds.",
          })}
          icon={<Icon name="creditCard" className="text-[6.25rem]" />}
        />
      }
    >
      {(validatedData) => <WithdrawTabView data={validatedData} />}
    </ProfileSection>
  );
}

function WithdrawTabView({ data }: { data: TPaymentMethodsList["data"] }) {
  const [paymentMethod, setPaymentMethod] = useState(first(data)?.id);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [isWithdrawWarningVisible, setWithdrawWarningVisible] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const { $t } = useTranslate();

  const { mutate: withdraw, isPending } = Payment.useWithdraw();
  const activeWithdrawRequestResponse = Payment.useActiveWithdrawRequest();

  const activeWithDrawRequest = activeWithdrawRequestResponse.data?.result?.data?.[0];
  const isWithdrawRequestActive = Boolean(activeWithDrawRequest);

  const selectedMethod = data.find((item) => item.id === Number(paymentMethod));
  const isDepositMethodWithLimits = Boolean(selectedMethod?.limits);
  const isPixMethod = selectedMethod?.type === "pix";
  const isPayPalMethod = selectedMethod?.type === "paypal";
  const isCryptoMethod = selectedMethod?.type === "crypto";

  let paymentAmountMin: number | undefined;
  let paymentAmountMax: number | undefined;
  if (isDepositMethodWithLimits) {
    paymentAmountMin = penniesToDollars(selectedMethod?.limits?.min);
    paymentAmountMax = penniesToDollars(selectedMethod?.limits?.max);
  }

  const PaymentAccountFieldComponent = selectPaymentAccountComponent(selectedMethod?.type);

  function requestWithdraw(data: FormData) {
    const withdrawParams = {
      methodId: Number(data.get("payment-method")),
      amount: Number(data.get("payment-amount")),
      account: String(data.get("payment-account")).trim(),
      ...(!isPixMethod &&
        !isPayPalMethod &&
        selectedMethod?.isBillingInfoRequired && {
          billingInfo: {
            firstName: String(data.get("billing-first-name")).trim(),
            lastName: String(data.get("billing-last-name")).trim(),
            address: String(data.get("billing-address-line1")).trim(),
            addressLine2: String(data.get("billing-address-line2")).trim(),
            city: String(data.get("billing-city")).trim(),
            country: String(data.get("billing-country")).trim(),
            state: String(data.get("billing-state")).trim(),
            zip: String(data.get("billing-postal-code")).trim(),
          },
        }),
      ...(isPixMethod && {
        billingInfoPix: {
          mobile: String(data.get("billing-pix-mobile")).trim(),
          email: String(data.get("billing-pix-email")).trim(),
          taxNumber: String(data.get("billing-pix-tax-number")).trim(),
        },
      }),
      ...(isPayPalMethod && {
        billingInfoPayPal: {
          firstName: String(data.get("billing-first-name")).trim(),
          lastName: String(data.get("billing-last-name")).trim(),
        },
      }),
      ...(isCryptoMethod && {
        cryptoAddressCurrency: String(data.get("crypt-billing-currency")).trim() || null,
      }),
    };

    withdraw(withdrawParams, {
      onSuccess: (data) => {
        if (data.error) {
          const { message, code } = data.error;
          emitter.emit("ERROR", { error: new APIError(message, { code }) });
        } else {
          formRef.current?.reset();
          activeWithdrawRequestResponse.refetch();
          emitter.emit("SUCCESS_MODAL_OPEN", {
            message: $t({ defaultMessage: "Withdrawal request sent" }),
          });
        }
      },
    });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!formRef.current) return;
    const data = new FormData(formRef.current);

    data.set("payment-amount", String(Number(data.get("payment-amount")) * 100));

    if (validateForm(data)) {
      requestWithdraw(data);
    } else {
      console.error("Form is invalid");
    }
  }

  function validatePaymentMethod(method: number) {
    return data.some((item) => item.id === method);
  }

  function validatePaymentAmount(amount: number, method: number) {
    const selectedMethod = data.find((item) => item.id === method);

    if (!selectedMethod) return false;

    const isDepositMethodWithLimits = Boolean(selectedMethod.limits);

    if (isDepositMethodWithLimits) {
      const min = selectedMethod.limits?.min ?? 0;
      const max = selectedMethod.limits?.max ?? 0;

      return amount && amount >= min && amount <= max;
    }
    return amount && amount > 0;
  }

  function validatePaymentAccount(account: string) {
    return account.length > 0;
  }

  function validateForm(data: FormData) {
    const paymentMethod = Number(data.get("payment-method"));
    const paymentAmount = Number(data.get("payment-amount"));
    const paymentAccount = data.get("payment-account") as string;

    return (
      validatePaymentMethod(paymentMethod) &&
      validatePaymentAmount(paymentAmount, paymentMethod) &&
      validatePaymentAccount(paymentAccount)
    );
  }

  useEffect(() => {
    const defaultAmount = selectedMethod?.limits?.default;
    if (defaultAmount !== undefined) {
      setPaymentAmount(String(penniesToDollars(defaultAmount)));
    }
  }, [paymentMethod]);

  useEffect(() => {
    setWithdrawWarningVisible(isWithdrawRequestActive);
  }, [isWithdrawRequestActive]);

  return (
    <div
      className={clsx(
        "gutter-stable relative min-h-0 py-1 mobile-only:pb-5 lg:h-full lg:max-h-full lg:overflow-y-auto",
        isPending && "pointer-events-none blur-sm",
      )}
    >
      <form className="text-14" onSubmit={handleSubmit} ref={formRef}>
        <p>
          {$t({
            defaultMessage:
              "You can make a withdrawal request. To do this, specify the amount requested for withdrawal.",
          })}
        </p>
        <hr style={{ borderColor: "var(--text-secondary-color)" }} className="my-3" />
        {isWithdrawWarningVisible && (
          <ActiveWithdrawWarning
            withdrawTimestamp={activeWithDrawRequest?.createdAt ?? ""}
            onClose={() => setWithdrawWarningVisible(false)}
          />
        )}
        <ol className="lg:list-outside list-inside list-decimal space-y-10 lg:ps-5">
          <li>
            <span>
              {$t({
                defaultMessage: "Choose one of the ways where you want to receive payment.",
              })}
            </span>
            <RadioGroup
              value={paymentMethod}
              onChange={setPaymentMethod}
              name="payment-method"
              className="mt-2.5"
            >
              <ul className="flex flex-wrap gap-2.5">
                {data.map(({ id, name, logoUrl, commission, limits, providerStringId }) => {
                  return (
                    <li key={id}>
                      <PaymentMethodCard
                        id={id}
                        name={name}
                        /*
                         * Для метода cryptpay и лобби указанных в задаче https://git.casinomodule.org/casino23/tickets/-/work_items/3902
                         * нужно использовать кастомный логотип.
                         */
                        logoUrl={
                          providerStringId === "cryptpay" && USE_REST_WITHDRAW
                            ? trcErcBancImage
                            : logoUrl
                        }
                        commission={commission}
                        limits={limits}
                      />
                    </li>
                  );
                })}
              </ul>
            </RadioGroup>
          </li>
          {isCryptoMethod ? (
            <>
              <li>
                <span>{$t({ defaultMessage: "Payment method" })}</span>
                <CryptoCurrencyBlock methodId={selectedMethod?.id ?? 0} />
              </li>
              <li>
                <span>{$t({ defaultMessage: "Wallet" })}</span>
                <CryptoWalletBlock />
              </li>
            </>
          ) : (
            <li>
              <PaymentAccountFieldComponent />
            </li>
          )}
          {isPixMethod && (
            <li>
              <span>
                {$t({
                  defaultMessage: "Add your billing information to show on invoice",
                })}
              </span>
              <PIXBillingInformation />
            </li>
          )}
          {isPayPalMethod && (
            <li>
              <span>
                {$t({
                  defaultMessage: "Add your billing information to show on invoice",
                })}
              </span>
              <PayPalBillingInformation />
            </li>
          )}
          {!isPixMethod && !isPayPalMethod && selectedMethod?.isBillingInfoRequired && (
            <li>
              <span>
                {$t({
                  defaultMessage: "Add your billing information to show on invoice",
                })}
              </span>
              <BillingInformation />
            </li>
          )}

          <li>
            <span>{$t({ defaultMessage: "Enter the amount you want to withdraw" })}</span>

            <Field disabled={!selectedMethod}>
              <Label className="block font-bold data-[disabled]:opacity-50">
                {$t({ defaultMessage: "Amount" })}
              </Label>
              <div className="flex mobile-only:flex-col gap-3 lg:gap-2.5 lg:items-center">
                <Input
                  className="input w-56 appearance-none data-[disabled]:opacity-50"
                  placeholder={$t({ defaultMessage: "Enter the amount" })}
                  name="payment-amount"
                  type="number"
                  value={paymentAmount}
                  pattern="[0-9]*"
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min={paymentAmountMin}
                  max={paymentAmountMax}
                  step={0.01}
                  required
                />
                <Button
                  variant="success"
                  type="submit"
                  disabled={!paymentAmount}
                  className="mobile-only:mx-auto mobile-only:w-fit"
                >
                  {$t({ defaultMessage: "Request withdraw" })}
                </Button>
              </div>
            </Field>
          </li>
        </ol>
      </form>

      {isPending && (
        <div className="fixed mobile-only:absolute inset-0 flex-center backdrop-blur-sm [margin:0!important]">
          <Spinner />
        </div>
      )}
    </div>
  );
}

function BillingInformation() {
  const { $t } = useTranslate();

  return (
    <div className="billing-information-block space-y-4">
      <div className="flex gap-2 *:flex-1">
        <div>
          <label>
            {$t({ defaultMessage: "First name" })}
            {" *"}
          </label>
          <input
            type="text"
            name="billing-first-name"
            placeholder={$t({ defaultMessage: "First name" })}
            autoComplete="given-name"
            minLength={2}
            maxLength={100}
            required
            pattern="\D{2,100}"
            title={$t({
              defaultMessage: "2 or more characters. Numbers are not allowed",
            })}
            className="input"
          />
        </div>
        <div>
          <label>
            {$t({ defaultMessage: "Last name" })}
            {" *"}
          </label>
          <input
            type="text"
            name="billing-last-name"
            placeholder={$t({ defaultMessage: "Last name" })}
            autoComplete="family-name"
            minLength={2}
            maxLength={100}
            required
            pattern="\D{2,100}"
            title={$t({
              defaultMessage: "2 or more characters. Numbers are not allowed",
            })}
            className="input"
          />
        </div>
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "Address" })}{" "}
          <span className="font-normal">
            {"("}
            {$t({ defaultMessage: "Street, P.O. box" })}
            {")"}
          </span>
          {" *"}
        </label>
        <input
          type="text"
          name="billing-address-line1"
          placeholder={$t({ defaultMessage: "Address" })}
          autoComplete="address-line1"
          minLength={5}
          maxLength={100}
          required
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({
            defaultMessage: "Address line 2",
          })}{" "}
          <span className="font-normal">
            {"("}
            {$t({ defaultMessage: "Apartment, suite, unit" })}
            {")"}
          </span>
        </label>
        <input
          type="text"
          name="billing-address-line2"
          placeholder={$t({
            defaultMessage: "Address line 2",
          })}
          autoComplete="address-line2"
          maxLength={100}
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "City" })}
          {" *"}
        </label>
        <input
          type="text"
          name="billing-city"
          placeholder={$t({ defaultMessage: "City" })}
          autoComplete="address-level2"
          minLength={3}
          maxLength={100}
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "Country/Region" })}
          {" *"}
        </label>
        <div className="select-wrapper">
          <select name="billing-country" autoComplete="address-level1" required defaultValue={""}>
            <option value="" disabled>
              {$t({ defaultMessage: "Select a country/region" })}
            </option>
            {countrySelectorOptions.map(([code, name, flag]) => (
              <option key={code} value={name}>
                {flag} {name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <div className="flex gap-2 *:flex-1">
          <div>
            <label>{$t({ defaultMessage: "State/Province" })}</label>
            <input
              type="text"
              name="billing-state"
              placeholder="State/Province"
              autoComplete="address-level1"
              maxLength={100}
              className="input"
            />
            <span
              style={{
                color: "var(--text-secondary-color)",
              }}
              className="text-[0.875em]"
            >
              {$t({
                defaultMessage: "Required for certain countries",
              })}
            </span>
          </div>
          <div>
            <label>
              {$t({
                defaultMessage: "Postal/Zip code",
              })}
              {" *"}
            </label>
            <input
              type="text"
              name="billing-postal-code"
              placeholder={$t({
                defaultMessage: "Postal/Zip code",
              })}
              autoComplete="postal-code"
              minLength={2}
              maxLength={100}
              required
              className="input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ActiveWithdrawWarning({
  withdrawTimestamp,
  onClose,
}: {
  withdrawTimestamp: string;
  onClose: () => void;
}) {
  const { $t } = useTranslate();
  const { intl } = useIntlT();
  return (
    <>
      <Alert
        type="warning"
        message={$t(
          {
            defaultMessage: `At the moment, you already have an active withdrawal request from {dateTime}.
                    If you make a new request, then the previous request will be canceled.`,
          },
          {
            dateTime: new Date(withdrawTimestamp).toLocaleString(intl.locale, {
              timeStyle: "short",
              dateStyle: "short",
            }),
          },
        )}
        afterClose={onClose}
      />
      <div className="mb-3" />
    </>
  );
}
